import { createSelector } from "@reduxjs/toolkit"
import { memoize, arrToMap, isDefined } from "nodash"
import {
    selectActiveWorkspaceId,
    selectAllWorkspaces,
    selectLegacyWorkspaceTree,
    selectTags,
} from "../WorkspaceManagementSlice"
import { selectAllFires } from "features/entity-caching/fires/selectors"
import { selectAllMembers } from "features/entity-caching/members/UserManagementSlice"
import {
    selectHasInitiallyFetchedMe,
    selectIsFetchingMe,
    selectUserID,
    selectUserProfileDefaultWorkspaceId,
} from "user/profile/selectors"
import { RootState } from "integrations/redux/rootReducer"
import { Folder } from "../types"

export const selectAllFoldersById = createSelector(
    [selectTags],
    (tags) => arrToMap(tags, "id") as Record<string, Folder>
)

export const selectSortedSidePanelRootFolders = createSelector(
    [selectLegacyWorkspaceTree],
    (tree) => [...(tree ?? [])].sort((a, b) => a.name.localeCompare(b.name)) as Folder[]
)

export const selectActiveProjectIds = createSelector([selectAllFires], (allProjectsById) =>
    Object.values(allProjectsById)
        .filter(isDefined)
        .filter((project) => project.viewers.length > 0)
        .map((project) => project.id)
)

export const selectFavoritesData = createSelector(
    [
        selectAllMembers(),
        selectActiveWorkspaceId(),
        selectUserID,
        selectAllFoldersById,
        selectAllFires,
    ],
    (allMembers, activeWorkspaceId, userId, allFoldersById, allProjectsById) => {
        const allMembersOfThisWorkspace = allMembers[activeWorkspaceId || ""]
        const activeMember = allMembersOfThisWorkspace?.find((m) => m.userId === userId)

        const allFolderIds = new Set(Object.keys(allFoldersById))
        const allProjectIds = new Set(Object.keys(allProjectsById))

        const sortedFavoriteIds =
            activeMember?.favorites?.filter(
                (id) => allFolderIds.has(id) || allProjectIds.has(id)
            ) ?? []

        const favoriteIds = new Set<string>()
        const sortedFavoriteFolderIds: string[] = []
        const sortedFavoriteProjectIds: string[] = []

        for (const id of sortedFavoriteIds) {
            favoriteIds.add(id)
            if (allFolderIds.has(id)) sortedFavoriteFolderIds.push(id)
            else if (allProjectIds.has(id)) sortedFavoriteProjectIds.push(id)
        }

        const sortedRecentProjectIds = Object.keys(JSON.parse(activeMember?.recents || "{}"))
            .filter((id) => allProjectIds.has(id))
            .sort((a, b) => b.localeCompare(a))

        return {
            sortedFavoriteIds,
            favoriteIds,
            sortedFavoriteFolderIds,
            sortedFavoriteProjectIds,
            sortedRecentProjectIds,
        }
    }
)

export const selectAllSubEntitiesByFolderId = memoize((folderId: string) =>
    createSelector([selectAllFoldersById], (allFoldersById) => {
        const folder = allFoldersById[folderId]
        if (!folder) return { subChains: [] as Folder["subTags"], projectIds: [] as string[] }
        return {
            subChains: folder.subTags,
            projectIds: folder.projects.map(({ id }) => id),
        }
    })
)

export const selectDefaultWorkspaceId = createSelector(
    [
        selectHasInitiallyFetchedMe,
        selectIsFetchingMe,
        selectUserProfileDefaultWorkspaceId,
        selectAllWorkspaces(),
        (_: RootState, recentWorkspaceId: string | null) => recentWorkspaceId,
    ],
    (hasFetchedMe, isFetchingMe, preferredWorkspaceId, allWorkspaces, recentWorkspaceId) => {
        if (!hasFetchedMe || isFetchingMe) return undefined

        const isKnownWorkspace = (id: string | null | undefined) =>
            allWorkspaces.some((workspace) => workspace.id === id)

        if (isKnownWorkspace(preferredWorkspaceId)) return preferredWorkspaceId
        if (isKnownWorkspace(recentWorkspaceId)) return recentWorkspaceId ?? undefined

        return [...allWorkspaces].sort((a, b) => a.name.localeCompare(b.name))[0]?.id
    }
)
